/** @jsxImportSource @emotion/react */
import React from "react";
import {betterMemo} from "js/common/utils/more-memo";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import {css} from "@emotion/react";
import {bullhornOrange} from "js/common/cube19-colors";
import UserAvatar from "js/common/views/user-avatar";

const wrappingDivStyle = css`
  position: relative;
  flex-shrink: 0;
`;

const crownStyle = css`
  position: absolute;
  top: -8px;
  left: -8px;
  z-index: 2;
`;

const avatarStyle = (theme) => css`
  object-position: center;
  object-fit: cover;
  margin-right: 10px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background-color: ${theme.palette.background.paper};
  display: block;
`;

const CrownIcon = ({primaryColour, backgroundColour}) => (
    <svg
        css={crownStyle}
        width="19"
        height="18"
        viewBox="0 0 19 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
      <path
          d="M13.0825 4.92069C12.9427 4.9326 12.8062 4.98318 12.6885 5.06667C12.5708 5.15017 12.4768 5.26321 12.4171 5.3929L11.2992 7.79917L8.63521 7.01009C8.50967 6.97432 8.37435 6.97305 8.24303 7.00641C8.11171 7.03978 7.98909 7.10658 7.88765 7.20001C7.7862 7.29345 7.70957 7.41018 7.66555 7.53832C7.62152 7.66646 7.61169 7.80142 7.63704 7.92947L8.20492 10.6493L5.71507 11.5659C5.58077 11.615 5.46023 11.6996 5.36709 11.8101C5.27395 11.9206 5.21192 12.0525 5.188 12.191C5.16409 12.3295 5.17925 12.4691 5.23176 12.5939C5.28428 12.7187 5.37206 12.8238 5.48519 12.8973L9.69271 15.6483C9.77396 15.7018 9.86652 15.7375 9.9648 15.7532C10.0631 15.7689 10.165 15.7643 10.2645 15.7397C10.3997 15.707 10.5261 15.6392 10.6305 15.5433C10.6811 15.4965 10.7253 15.4434 10.7619 15.3855C12.0221 13.4416 13.7655 11.8358 15.8062 10.7394C15.9908 10.6373 16.1324 10.4695 16.2 10.2726C16.2335 10.1756 16.2469 10.0743 16.2393 9.97491C16.2317 9.87554 16.2033 9.78035 16.1559 9.69532L13.7629 5.27302C13.6986 5.15448 13.6008 5.05866 13.4806 4.9964C13.3604 4.93414 13.2225 4.90792 13.0825 4.92069Z"
          fill={primaryColour} />

      {/* Crown jewels */}
      <circle
          cx="7.06768"
          cy="6.30999"
          r="1.61362"
          transform="rotate(-42.6473 7.06768 6.30999)"
          fill={primaryColour} />
      <circle
          cx="13.1467"
          cy="4.27583"
          r="1.00851"
          transform="rotate(-42.6473 13.1467 4.27583)"
          fill={primaryColour} />
      <circle
          cx="4.54175"
          cy="12.2011"
          r="1.00851"
          transform="rotate(-42.6473 4.54175 12.2011)"
          fill={primaryColour} />

      {/* Crown highlights */}
      <circle
          cx="10.8937"
          cy="10.4638"
          r="1.21021"
          transform="rotate(-42.6473 10.8937 10.4638)"
          fill={backgroundColour} />
      <circle
          cx="9.07817"
          cy="12.959"
          r="0.605107"
          transform="rotate(-42.6473 9.07817 12.959)"
          fill={backgroundColour} />
      <circle
          cx="13.5293"
          cy="8.85935"
          r="0.605107"
          transform="rotate(-42.6473 13.5293 8.85935)"
          fill={backgroundColour} />
    </svg>
);

const LeaderboardAvatar = betterMemo(
    {displayName: "LeaderboardAvatar"},
    ({rank, user}) => {
      const {theme} = React.useContext(CustomThemeContext);
      const isFirstPlace = rank === 1;

      return (
          <div css={wrappingDivStyle}>
            {isFirstPlace && <CrownIcon
                primaryColour={bullhornOrange}
                backgroundColour={theme.palette.background.card} />}

            <UserAvatar
                user={user}
                css={avatarStyle(theme)}
                eagerLoading={true}
                useThumbnail={true} />
          </div>);
    });

export default LeaderboardAvatar;
